import { getFirmFileExtension } from "@/lib/utils/firmFile";
import { getFileNameParts } from "@/lib/utils/matterFile";
import type { SignatureRequestPreviewDocument } from "./steps/SignatureRequestDocumentPreview";

export const signatureRequestCreationSteps = [
  {
    id: "select-document",
    title: "Select document",
  },
  {
    id: "destination",
    title: "Destination",
  },
  {
    id: "map-variables",
    title: "Map variables",
  },
  {
    id: "send-request",
    title: "Add signers & fields",
  },
  {
    id: "review-send",
    title: "Review & send",
  },
] as const;

export const mergeFirmFileVariableLists = (
  ...lists: ReadonlyArray<string[] | undefined | null>
): string[] => {
  const unique = new Set<string>();
  for (const list of lists) {
    for (const v of list ?? []) {
      unique.add(v);
    }
  }
  return [...unique];
};

export const getSelectedDocumentFileName = (
  selectedDocument: SignatureRequestPreviewDocument | null,
) => {
  if (!selectedDocument) {
    return "";
  }

  switch (selectedDocument.type) {
    case "matter-file":
      return selectedDocument.document.name;
    case "matter-template":
      return selectedDocument.document.name;
    case "firm-file":
      return selectedDocument.document.name;
    default:
      return "";
  }
};

export const getEffectiveSignatureRequestSteps = (
  selectedDocument: SignatureRequestPreviewDocument | null,
) =>
  selectedDocument?.type === "matter-template"
    ? signatureRequestCreationSteps
    : signatureRequestCreationSteps.filter(
        (step) => step.id !== "map-variables",
      );

export const isSignatureRequestDocumentPdf = (
  selectedDocument: SignatureRequestPreviewDocument | null,
) =>
  selectedDocument?.type === "firm-file"
    ? getFirmFileExtension(selectedDocument.document).toLowerCase() === "pdf"
    : selectedDocument
      ? getFileNameParts(
          selectedDocument.document.name,
        ).fileExtension.toLowerCase() === "pdf"
      : false;

export const buildUniqueGeneratedFileName = ({
  existingFileNames,
  outputExtension,
  sourceFileName,
}: {
  existingFileNames: Array<string>;
  outputExtension: ".docx" | ".pdf";
  sourceFileName?: string;
}) => {
  const { fileName } = getFileNameParts(
    sourceFileName ?? "signature-request-document.docx",
  );
  const normalizedBaseName = `${fileName || "signature-request-document"} - Signature Request`;
  const normalizedExtension = outputExtension;
  const normalizedExistingNames = new Set(
    existingFileNames.map((name) => name.toLowerCase()),
  );

  let candidate = `${normalizedBaseName}${normalizedExtension}`;
  let index = 1;

  while (normalizedExistingNames.has(candidate.toLowerCase())) {
    candidate = `${normalizedBaseName} (${index})${normalizedExtension}`;
    index += 1;
  }

  return candidate;
};

export const buildUniqueGeneratedFirmFileName = ({
  existingFileNames,
  outputExtension,
  sourceFileName,
}: {
  existingFileNames: Array<string>;
  outputExtension: ".docx" | ".pdf";
  sourceFileName?: string;
}) => {
  const { fileName } = getFileNameParts(
    sourceFileName ?? "signature-request-document.docx",
  );
  const normalizedBaseName = `${fileName || "signature-request-document"} - Signature Request`;
  const normalizedExtension = outputExtension;
  const normalizedExistingNames = new Set(
    existingFileNames.map((name) => name.toLowerCase()),
  );

  let candidate = `${normalizedBaseName}${normalizedExtension}`;
  let index = 1;

  while (normalizedExistingNames.has(candidate.toLowerCase())) {
    candidate = `${normalizedBaseName} (${index})${normalizedExtension}`;
    index += 1;
  }

  return candidate;
};
